import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../../../config/firebaseConfig";
import { fetchParentsStudents } from "../../../redux/features/parentStudentsSlice";
import { getStudioInfoForGrade } from "../utils/workspaceUtils";

/**
 * Hook to manage student accounts, parent-child selection, and studio environment resolution.
 */
export const useWorkspaceStudent = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const childIdParam = searchParams.get("childId");

  const { userDetails: parentsDetails } = useSelector((state) => state.auth);
  const { students: reduxStudents } = useSelector(
    (state) => state.parentStudents || { students: [] }
  );

  const [childrenList, setChildrenList] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [activeWorkspaceEnv, setActiveWorkspaceEnv] = useState("scratch");
  const [showAddChildModal, setShowAddChildModal] = useState(false);

  const isStudentAccount = parentsDetails?.accountType === "student";

  const initEnvForChild = useCallback((child) => {
    if (!child) return;
    if (child.workspace) {
      setActiveWorkspaceEnv(child.workspace);
    } else {
      const activeGrade = child.grade || child.studentClass || child.class;
      const studioInfo = getStudioInfoForGrade(activeGrade);
      if (studioInfo.type === "python") {
        setActiveWorkspaceEnv("python");
      } else if (studioInfo.type === "junior") {
        setActiveWorkspaceEnv("scratch_jr");
      } else {
        setActiveWorkspaceEnv("scratch");
      }
    }
  }, []);

  // Fetch children from Firestore students collection or student account session
  useEffect(() => {
    const fetchChildren = async () => {
      const parentId =
        parentsDetails?.id || parentsDetails?.uid || auth?.currentUser?.uid;
      if (!parentId) return;

      // If logged in directly as student, isolate context to this student
      if (isStudentAccount) {
        setChildrenList([parentsDetails]);
        setSelectedChild(parentsDetails);
        initEnvForChild(parentsDetails);
        return;
      }

      try {
        const q = query(
          collection(db, "students"),
          where("parentUid", "==", parentId)
        );
        const snap = await getDocs(q);
        const list = [];
        snap.docs.forEach((docSnap) => {
          list.push({ id: docSnap.id, ...docSnap.data() });
        });

        if (list.length > 0) {
          setChildrenList(list);
          if (childIdParam) {
            const found = list.find(
              (c) => c.id === childIdParam || c.uid === childIdParam
            );
            setSelectedChild(found || list[0]);
            initEnvForChild(found || list[0]);
          } else {
            setSelectedChild(list[0]);
            initEnvForChild(list[0]);
          }
        } else if (Array.isArray(reduxStudents) && reduxStudents.length > 0) {
          setChildrenList(reduxStudents);
          setSelectedChild(reduxStudents[0]);
          initEnvForChild(reduxStudents[0]);
        }
      } catch (err) {
        console.error("Error fetching students in workspace:", err);
      }
    };

    fetchChildren();
  }, [parentsDetails, childIdParam, reduxStudents, isStudentAccount, initEnvForChild]);

  const handleChildAdded = useCallback(
    (newChild) => {
      setChildrenList((prev) => [...prev, newChild]);
      setSelectedChild(newChild);
      if (newChild.id) {
        setSearchParams({ childId: newChild.id });
      }
      const parentId =
        parentsDetails?.id || parentsDetails?.uid || auth?.currentUser?.uid;
      if (parentId) {
        dispatch(fetchParentsStudents({ parentUid: parentId }));
      }
    },
    [parentsDetails, dispatch, setSearchParams]
  );

  return {
    parentsDetails,
    childrenList,
    selectedChild,
    setSelectedChild,
    activeWorkspaceEnv,
    setActiveWorkspaceEnv,
    isStudentAccount,
    showAddChildModal,
    setShowAddChildModal,
    handleChildAdded,
    initEnvForChild,
  };
};

export default useWorkspaceStudent;
